import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import '@sweetalert2/theme-dark/dark.min.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faPlus} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

function ScheduleManagement() {
    const [schedules, setSchedules] = useState([]);
    const [ponds, setPonds] = useState([]);
    const [selectedPond, setSelectedPond] = useState('');
    const [peripherals, setPeripherals] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [statusBusyId, setStatusBusyId] = useState(null);
    const [formData, setFormData] = useState({
        pond_id: '',
        peripheral_id: '',
        schedule_1: null,
        schedule_2: null,
        schedule_3: null,
        schedule_4: null,
        durationMinutes: 30,
        isActive: true
    });
    const [editing, setEditing] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        fetchPonds();
        fetchSchedules();
    }, []);

    useEffect(() => {
        if (selectedPond) {
            fetchPeripheralsByPond(selectedPond);
            fetchSchedules(selectedPond);
        } else {
            fetchSchedules();
        }
    }, [selectedPond]);

    const fetchPonds = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/pond/all`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setPonds(response.data);
        } catch (error) {
            Swal.fire('Error', 'Failed to fetch ponds.', 'error');
        }
    };

    const handleToggleStatus = async (schedule) => {
        const token = localStorage.getItem('token');
        const nextIsActive = !schedule.isActive;
        const headers = { Authorization: `Bearer ${token}` };

        try {
            setStatusBusyId(schedule._id);
            // Optimistic update
            setSchedules(prev =>
                prev.map(s => (s._id === schedule._id ? { ...s, isActive: nextIsActive } : s))
            );

            const url = `${process.env.REACT_APP_BASE_URL}/api/schedules/${schedule._id}/status`;
            const { data } = await axios.patch(url, { isActive: nextIsActive }, { headers });

            // If backend returns the updated schedule, replace it to keep in sync
            if (data?.schedule?._id) {
                setSchedules(prev =>
                    prev.map(s => (s._id === schedule._id ? data.schedule : s))
                );
            }
        } catch (error) {
            // Revert on failure
            setSchedules(prev =>
                prev.map(s => (s._id === schedule._id ? { ...s, isActive: !nextIsActive } : s))
            );
            const msg = error.response?.data?.message || 'Failed to change status.';
            Swal.fire('Error', msg, 'error');
        } finally {
            setStatusBusyId(null);
        }
    };


    const fetchPeripheralsByPond = async (pondId) => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/peripherals/pond/${pondId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setPeripherals(response.data);
        } catch (error) {
            Swal.fire('Error', 'Failed to fetch peripherals for this pond.', 'error');
        }
    };

    const fetchSchedules = async (pondId = null) => {
        setIsLoading(true);
        const token = localStorage.getItem('token');
        try {
            const url = pondId
                ? `${process.env.REACT_APP_BASE_URL}/api/schedules/pond/${pondId}`
                : `${process.env.REACT_APP_BASE_URL}/api/schedules`;

            const response = await axios.get(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setSchedules(response.data);
        } catch (error) {
            Swal.fire('Error', 'Failed to fetch schedules.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox'
                ? checked
                : (value === '' ? null : value)
        }));
    };

    const handlePondChange = (e) => {
        const pondId = e.target.value;
        setSelectedPond(pondId);
        setFormData(prev => ({
            ...prev,
            pond_id: pondId,
            peripheral_id: '' // Reset peripheral when pond changes
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        try {
            if (editing) {
                await axios.put(`${process.env.REACT_APP_BASE_URL}/api/schedules/${editing._id}`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                Swal.fire('Success', 'Schedule updated successfully', 'success');
            } else {
                await axios.post(`${process.env.REACT_APP_BASE_URL}/api/schedules`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                Swal.fire('Success', 'Schedule created successfully', 'success');
            }
            resetForm();
            fetchSchedules(selectedPond);
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Failed to save schedule';
            Swal.fire('Error', errorMsg, 'error');
        }
    };

    const handleEdit = (schedule) => {
        setFormData({
            pond_id: schedule.pond_id._id || schedule.pond_id,
            peripheral_id: schedule.peripheral_id._id || schedule.peripheral_id,
            schedule_1: schedule.schedule_1 || null,
            schedule_2: schedule.schedule_2 || null,
            schedule_3: schedule.schedule_3 || null,
            schedule_4: schedule.schedule_4 || null,
            durationMinutes: schedule.durationMinutes || 30,
            isActive: schedule.isActive || false
        });
        setSelectedPond(schedule.pond_id._id || schedule.pond_id);
        setEditing(schedule);
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        const token = localStorage.getItem('token');
        try {
            const result = await Swal.fire({
                title: 'Are you sure?',
                text: 'This will permanently delete the schedule!',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Delete'
            });

            if (result.isConfirmed) {
                await axios.delete(`${process.env.REACT_APP_BASE_URL}/api/schedules/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                Swal.fire('Deleted!', 'Schedule has been deleted.', 'success');
                fetchSchedules(selectedPond);
            }
        } catch (error) {
            Swal.fire('Error', 'Failed to delete schedule.', 'error');
        }
    };

    const resetForm = () => {
        setFormData({
            pond_id: selectedPond || '',
            peripheral_id: '',
            schedule_1: null,
            schedule_2: null,
            schedule_3: null,
            schedule_4: null,
            durationMinutes: 30,
            isActive: true
        });
        setEditing(null);
        setIsModalOpen(false);
    };

    const openModal = () => {
        resetForm();
        setIsModalOpen(true);
    };

    const formatTime = (timeString) => {
        if (!timeString) return 'Not set';
        return new Date(`1970-01-01T${timeString}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const getPondName = (pond) => {
        if (!pond) return 'Unknown';
        if (typeof pond === 'object') return pond.name;

        const pondObj = ponds.find(p => p._id === pond);
        return pondObj ? pondObj.name : 'Unknown Pond';
    };

    const getPeripheralName = (peripheral) => {
        if (!peripheral) return 'Unknown';
        if (typeof peripheral === 'object') return `${peripheral.name}`;

        const peripheralObj = peripherals.find(p => p._id === peripheral);
        return peripheralObj ? `${peripheralObj.name}` : 'Unknown Peripheral';
    };

    return (
        <div className="container mx-auto p-4 md:p-6">
            <div className="container mx-auto bg-white rounded-lg shadow-lg p-4 md:p-6 mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full"></div>
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                        <FontAwesomeIcon icon={faClock} className="text-blue-500"/>
                        {t('schedule_management')}
                    </h1>
                </div>
            </div>

            {/* Pond Selection */}
            <div className="mb-6 bg-white  p-4 rounded-lg shadow">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="w-full md:w-1/3">
                        <label className="block text-gray-700  mb-2 font-medium">
                            {t('select_pond')}
                        </label>
                        <select
                            value={selectedPond}
                            onChange={handlePondChange}
                            className="w-full p-2 border border-gray-300  rounded "
                        >
                            <option value="">{t('all_ponds')}</option>
                            {ponds.map(pond => (
                                <option key={pond._id} value={pond._id}>
                                    {pond.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <button
                        onClick={openModal}
                        disabled={!selectedPond}
                        className="bg-blue-500 hover:bg-blue-600 text-white p-2 md:p-3 rounded transition duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
                        title={!selectedPond ? "Please select a pond first" : ""}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        <span>{t('add_new_schedule')}</span>
                    </button>
                </div>
            </div>

            {/* Schedules Table */}
            {/* Schedules Table */}
            <div className="bg-white/70  backdrop-blur-md shadow-sm ring-1 ring-black/5 rounded-xl overflow-hidden">
                <div className="px-4 md:px-6 py-4 border-b border-gray-100  flex items-center justify-between">
                    <h2 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white">
                        {t('schedule_list')}
                    </h2>
                </div>

                {isLoading ? (
                    <div className="p-8 flex items-center justify-center">
                        <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-gray-300 border-t-transparent " />
                    </div>
                ) : schedules.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="min-w-[980px] w-full text-sm">
                            {/* Sticky, glassy header */}
                            <thead className="sticky top-0 z-10 bg-white/75  supports-[backdrop-filter]:backdrop-blur">
                            <tr className="text-center text-gray-600 ">
                                <th className="py-3.5 px-4 font-medium">{t('pond')}</th>
                                <th className="py-3.5 px-4 font-medium">{t('peripheral')}</th>
                                <th className="py-3.5 px-4 font-medium">{t('schedule_1')}</th>
                                <th className="py-3.5 px-4 font-medium">{t('schedule_2')}</th>
                                <th className="py-3.5 px-4 font-medium">{t('schedule_3')}</th>
                                <th className="py-3.5 px-4 font-medium">{t('schedule_4')}</th>
                                <th className="py-3.5 px-4 font-medium">{t('duration')}</th>
                                <th className="py-3.5 px-4 font-medium">{t('status')}</th>
                                <th className="py-3.5 px-4 font-medium">{t('actions')}</th>
                            </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-100 ">
                            {schedules.map((schedule) => {
                                const timeChip = (value) => (
                                    <span
                                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium
                ${value
                                            ? 'bg-slate-100 text-slate-700  ring-1 ring-slate-200/60 '
                                            : 'bg-gray-50 text-gray-400  ring-1 ring-gray-200/60 '
                                        }`}
                                        title={value ? formatTime(value) : 'Not set'}
                                    >
                {value ? formatTime(value) : '—'}
              </span>
                                );

                                return (
                                    <tr
                                        key={schedule._id}
                                        className="hover:bg-gray-50/60  transition-colors"
                                    >
                                        <td className="py-3.5 px-4 text-centertext-gray-900 ">
                                            <div className="font-medium text-center">{getPondName(schedule.pond_id)}</div>
                                        </td>

                                        <td className="py-3.5 px-4 text-gray-700  text-center">
                                            {getPeripheralName(schedule.peripheral_id)}
                                        </td>

                                        <td className="py-3.5 px-4 text-center">{timeChip(schedule.schedule_1)}</td>
                                        <td className="py-3.5 px-4 text-center">{timeChip(schedule.schedule_2)}</td>
                                        <td className="py-3.5 px-4 text-center">{timeChip(schedule.schedule_3)}</td>
                                        <td className="py-3.5 px-4 text-center">{timeChip(schedule.schedule_4)}</td>

                                        <td className="py-3.5 px-4 text-center text-gray-800  whitespace-nowrap">
                  <span className="inline-flex items-center rounded-md bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-200 px-2 py-1 text-xs ">
                    {schedule.durationMinutes} {t('mins') || 'mins'}
                  </span>
                                        </td>

                                        <td className="py-3.5 px-4 text-center">
                                            <button
                                                onClick={() => handleToggleStatus(schedule)}
                                                disabled={statusBusyId === schedule._id}
                                                title={schedule.isActive ? 'Disable schedule' : 'Enable schedule'}
                                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition
                      ${schedule.isActive
                                                    ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 ring-1 ring-emerald-200 '
                                                    : 'bg-rose-50 text-rose-700 hover:bg-rose-100 ring-1 ring-rose-200 '}
                      ${statusBusyId === schedule._id ? 'opacity-70 cursor-wait' : ''}`}
                                            >
                    <span
                        className={`inline-block h-2.5 w-2.5 rounded-full ${schedule.isActive ? 'bg-emerald-500' : 'bg-rose-500'}`}
                    />
                                                {statusBusyId === schedule._id
                                                    ? (t('please_wait') || 'Please wait…')
                                                    : schedule.isActive
                                                        ? (t('active') || 'Active')
                                                        : (t('inactive') || 'Inactive')}
                                            </button>
                                        </td>

                                        <td className="py-3.5 px-4 text-center">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => handleEdit(schedule)}
                                                    className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium
                        bg-blue-50 text-blue-700 hover:bg-blue-100 ring-1 ring-inset ring-blue-200
                       "
                                                    title="Edit"
                                                >
                                                    <svg viewBox="0 0 24 24" className="h-4 w-4 mr-1" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM21.41 6.34a1.25 1.25 0 000-1.77L19.43 2.59a1.25 1.25 0 00-1.77 0L16 4.25l3.75 3.75 1.66-1.66z"/></svg>
                                                    {t('edit')}
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(schedule._id)}
                                                    className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium
                        bg-rose-50 text-rose-700 hover:bg-rose-100 ring-1 ring-inset ring-rose-200
                        "
                                                    title="Delete"
                                                >
                                                    <svg viewBox="0 0 24 24" className="h-4 w-4 mr-1" fill="currentColor"><path d="M6 7h12v14H6z" opacity=".3"/><path d="M16 1H8L7 2H2v2h20V2h-5zM6 7h12v14H6z"/></svg>
                                                    {t('delete')}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="px-6 py-14 text-center">
                        <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-gray-100  flex items-center justify-center">
                            <FontAwesomeIcon icon={faClock} className="text-gray-500 " />
                        </div>
                        <p className="text-gray-700 ">
                            {selectedPond ? 'No schedules found for this pond' : 'No schedules found'}
                        </p>
                    </div>
                )}
            </div>


            {/* Schedule Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                            <div className="absolute inset-0 bg-gray-500 opacity-75 "></div>
                        </div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                        <div className="inline-block align-bottom bg-white  rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                            <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <h3 className="text-lg leading-6 font-medium text-gray-900  mb-4">
                                    {editing ? t('edit_schedule') : t('add_new_schedule')}
                                </h3>
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Pond Field (disabled when editing) */}
                                        <div className="mb-4">
                                            <label className="block text-gray-700  mb-2">
                                                {t('pond')}
                                            </label>
                                            <select
                                                value={formData.pond_id}
                                                onChange={handlePondChange}
                                                className="w-full p-2 border border-gray-300  rounded "
                                                required
                                                disabled={!!editing}
                                            >
                                                <option value="">{t('select_pond')}</option>
                                                {ponds.map(pond => (
                                                    <option key={pond._id} value={pond._id}>
                                                        {pond.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Peripheral Field */}
                                        <div className="mb-4">
                                            <label className="block text-gray-700  mb-2">
                                                {t('peripheral')}
                                            </label>
                                            <select
                                                name="peripheral_id"
                                                value={formData.peripheral_id}
                                                onChange={handleChange}
                                                className="w-full p-2 border border-gray-300 "
                                                required
                                            >
                                                <option value="">{t('select_peripheral')}</option>
                                                {peripherals.map(peripheral => (
                                                    <option key={peripheral._id} value={peripheral._id}>
                                                        {peripheral.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Duration */}
                                        <div className="mb-4">
                                            <label className="block text-gray-700  mb-2">
                                                {t('duration_minutes')}
                                            </label>
                                            <input
                                                type="number"
                                                name="durationMinutes"
                                                min="1"
                                                value={formData.durationMinutes}
                                                onChange={handleChange}
                                                className="w-full p-2 border border-gray-300  rounded "
                                                required
                                            />
                                        </div>

                                        {/* Active Toggle */}
                                        <div className="mb-4 flex items-center">
                                            <input
                                                type="checkbox"
                                                name="isActive"
                                                id="isActive"
                                                checked={formData.isActive}
                                                onChange={handleChange}
                                                className="mr-2 h-5 w-5 text-blue-500 rounded focus:ring-blue-400"
                                            />
                                            <label htmlFor="isActive" className="text-gray-700 ">
                                                {t('active_schedule')}
                                            </label>
                                        </div>

                                        {/* Schedule Times */}
                                        {[1, 2, 3, 4].map(num => (
                                            <div className="mb-4" key={num}>
                                                <label className="block text-gray-700 0 mb-2">
                                                    {t(`schedule_${num}`)}
                                                </label>
                                                <input
                                                    type="time"
                                                    name={`schedule_${num}`}
                                                    value={formData[`schedule_${num}`]}
                                                    onChange={handleChange}
                                                    className="w-full p-2 border border-gray-300  rounded "
                                                />
                                            </div>
                                        ))}
                                    </div>

                                    <div className="flex justify-end space-x-4 pt-4">
                                        <button
                                            type="button"
                                            onClick={resetForm}
                                            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition duration-200"
                                        >
                                            {t('cancel')}
                                        </button>
                                        <button
                                            type="submit"
                                            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition duration-200"
                                        >
                                            {editing ? t('update') : t('save')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ScheduleManagement;